import { create } from 'zustand';

export interface Post {
    id: string;
    authorName: string;
    authorAvatar?: string;
    authorRole?: string;
    content: string;
    timestamp: string;
    likes: number;
    comments: number;
    hasVideo: boolean;
    hasStats: boolean;
}

export interface Comment {
    id: string;
    postId: string;
    authorName: string;
    content: string;
    timestamp: string;
}

interface SocialState {
    posts: Post[];
    likedPostIds: Set<string>;
    bookmarkedPostIds: Set<string>;
    followingIds: Set<string>;
    commentsByPost: Record<string, Comment[]>;

    // Actions
    fetchFeed: () => void;
    likePost: (postId: string) => void;
    bookmarkPost: (postId: string) => void;
    addComment: (postId: string, content: string, authorName: string) => void;
    followUser: (userId: string) => void;
    createPost: (post: Omit<Post, 'id' | 'timestamp' | 'likes' | 'comments'>) => void;
}

// Initial mock data
const INITIAL_POSTS: Post[] = [
    {
        id: '1',
        authorName: 'Coach Miller',
        authorRole: 'Head Coach',
        content: 'Huge shoutout to Marcus for pinning his way through the holiday tournament. The high crotch setups from the outside were flawless today. Back to the lab on Monday. 🥇🏆',
        timestamp: '2h ago',
        likes: 24,
        comments: 3,
        hasVideo: true,
        hasStats: false,
    },
    {
        id: '2',
        authorName: 'Alex Mercer',
        authorRole: 'Athlete',
        content: 'Hit 100 takedowns in live goes this week. Consistency is the only metric that matters.',
        timestamp: '5h ago',
        likes: 12,
        comments: 0,
        hasVideo: false,
        hasStats: true,
    }
];

export const useSocialStore = create<SocialState>((set) => ({
    posts: INITIAL_POSTS,
    likedPostIds: new Set(),
    bookmarkedPostIds: new Set(),
    followingIds: new Set(),
    commentsByPost: {},

    fetchFeed: () => {
        // In reality, fetch from Supabase. For MVP, we use initial state.
    },

    likePost: (postId) => set((state) => {
        const newLikes = new Set(state.likedPostIds);
        const posts = [...state.posts];
        const postIdx = posts.findIndex(p => p.id === postId);

        if (postIdx === -1) return state;

        const post = posts[postIdx];
        if (!post) return state;

        if (newLikes.has(postId)) {
            newLikes.delete(postId);
            posts[postIdx] = { ...post, likes: post.likes - 1 };
        } else {
            newLikes.add(postId);
            posts[postIdx] = { ...post, likes: post.likes + 1 };
        }

        return { likedPostIds: newLikes, posts };
    }),

    bookmarkPost: (postId) => set((state) => {
        const newBookmarks = new Set(state.bookmarkedPostIds);
        if (newBookmarks.has(postId)) {
            newBookmarks.delete(postId);
        } else {
            newBookmarks.add(postId);
        }
        return { bookmarkedPostIds: newBookmarks };
    }),

    addComment: (postId, content, authorName) => set((state) => {
        const newComment: Comment = {
            id: Date.now().toString(),
            postId,
            authorName,
            content,
            timestamp: 'Just now'
        };

        const existingComments = state.commentsByPost[postId] || [];

        return {
            commentsByPost: {
                ...state.commentsByPost,
                [postId]: [...existingComments, newComment]
            }
        };
    }),

    followUser: (userId) => set((state) => {
        const newFollowing = new Set(state.followingIds);
        if (newFollowing.has(userId)) {
            newFollowing.delete(userId);
        } else {
            newFollowing.add(userId);
        }
        return { followingIds: newFollowing };
    }),

    createPost: (postData) => set((state) => {
        const newPost: Post = {
            ...postData,
            id: Date.now().toString(),
            timestamp: 'Just now',
            likes: 0,
            comments: 0,
        };
        return { posts: [newPost, ...state.posts] };
    })
}));
